// client/src/coms/organisation/orgApi.js
// API helpers for the organisation features (library, storage, bookmarks, search, channels,
// resources). Relative URLs only. Uploads use XHR so they can report progress and be cancelled.
import { comsApiGet, comsApiPost, comsApiPatch } from '../comsApi';

export { comsApiGet as apiGet, comsApiPost as apiPost, comsApiPatch as apiPatch };

async function send(method, path, body) {
  const res = await fetch(`/api/coms${path}`, {
    method,
    credentials: 'include',
    headers: { Accept: 'application/json', ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try {
    data = await res.json();
  } catch {}
  if (!res.ok) {
    const err = new Error((data && data.error) || `Request failed (${res.status})`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export const apiPut = (path, body) => send('PUT', path, body || {});
export const apiDelete = (path, body) => send('DELETE', path, body);
export const apiPostJson = (path, body) => send('POST', path, body || {});
export const apiPatchJson = (path, body) => send('PATCH', path, body || {});
export const apiGetJson = (path) => send('GET', path);

// Multipart upload to a Voxemera endpoint with progress. Returns { promise, abort }.
export function uploadForm(path, files, fields = {}, onProgress) {
  const xhr = new XMLHttpRequest();
  const promise = new Promise((resolve, reject) => {
    const form = new FormData();
    for (const [k, v] of Object.entries(fields)) if (v != null) form.append(k, String(v));
    for (const f of files) form.append(f.field || 'files', f.file || f, (f.file || f).name);
    xhr.open('POST', `/api/coms${path}`);
    xhr.withCredentials = true;
    xhr.setRequestHeader('Accept', 'application/json');
    if (xhr.upload && onProgress) xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onload = () => {
      let data = null;
      try {
        data = JSON.parse(xhr.responseText);
      } catch {}
      if (xhr.status >= 200 && xhr.status < 300) resolve(data);
      else {
        const err = new Error((data && data.error) || `Upload failed (${xhr.status})`);
        err.status = xhr.status;
        err.data = data;
        reject(err);
      }
    };
    xhr.onerror = () => reject(Object.assign(new Error('Network error during upload'), { status: 0 }));
    xhr.onabort = () => reject(Object.assign(new Error('Upload cancelled'), { cancelled: true }));
    xhr.send(form);
  });
  return { promise, abort: () => xhr.abort() };
}

// Sends bytes straight to a storage provider's upload URL (no Voxemera relay). Supports single requests,
// OneDrive-style chunked sessions and S3 multipart parts. Returns { promise, abort }.
export function directUpload(upload, file, onProgress) {
  let current = null;
  let cancelled = false;
  const put = (method, url, body, headers = {}, done = 0) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      current = xhr;
      xhr.open(method, url);
      for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
      if (xhr.upload && onProgress) xhr.upload.onprogress = (e) => e.lengthComputable && onProgress((done + e.loaded) / file.size);
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          let data = null;
          try {
            data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
          } catch {}
          resolve({ data, etag: xhr.getResponseHeader('ETag') });
        } else if (xhr.status === 202 || xhr.status === 308) resolve({ data: null, partial: true });
        else reject(Object.assign(new Error(`The storage provider refused the upload (${xhr.status})`), { status: xhr.status }));
      };
      xhr.onerror = () => reject(new Error('The upload to the storage provider failed (network or CORS)'));
      xhr.onabort = () => reject(Object.assign(new Error('Upload cancelled'), { cancelled: true }));
      xhr.send(body);
    });
  const promise = (async () => {
    if (upload.multipart) {
      const etags = [];
      for (let i = 0; i < upload.partUrls.length; i += 1) {
        if (cancelled) throw Object.assign(new Error('Upload cancelled'), { cancelled: true });
        const start = i * upload.partSize;
        const r = await put('PUT', upload.partUrls[i], file.slice(start, start + upload.partSize), {}, start);
        etags.push(String(r.etag || '').replace(/"/g, ''));
      }
      return { etags };
    }
    if (upload.chunked) {
      const size = upload.chunkSize || 5 * 1024 * 1024;
      let last = null;
      for (let start = 0; start < file.size; start += size) {
        if (cancelled) throw Object.assign(new Error('Upload cancelled'), { cancelled: true });
        const end = Math.min(file.size, start + size);
        const r = await put('PUT', upload.url, file.slice(start, end), { 'Content-Range': `bytes ${start}-${end - 1}/${file.size}` }, start);
        if (r.data) last = r.data;
      }
      return last || {};
    }
    const r = await put(upload.method || 'PUT', upload.url, file, upload.headers || {});
    return r.data || {};
  })();
  return {
    promise,
    abort: () => {
      cancelled = true;
      if (current) current.abort();
    },
  };
}

export function serverPath(serverKey) {
  return `/servers/${encodeURIComponent(String(serverKey || ''))}`;
}
